import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { cfoAPI } from '../services/api';

export default function CFODashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const response = await cfoAPI.getInsights();
      setData(response.data);
    } catch (error) {
      console.error('Error fetching CFO insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getInsightColor = (type) => {
    switch(type) {
      case 'warning': return '#EF4444';
      case 'success': return '#10B981';
      case 'info': return '#3B82F6';
      default: return '#6B7280';
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000000' }}>
        <div style={{ color: 'white' }}>Loading CFO Insights...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000000', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        padding: '2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem',
        borderBottom: '1px solid #333'
      }}>
        <div>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: 'white',
            margin: 0
          }}>
            CFO-in-a-Box
          </h1>
          <p style={{ color: '#999', marginTop: '0.5rem' }}>Your Automated Financial Partner</p>
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              backgroundColor: '#2D2D2D',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            ← Back to Dashboard
          </button>
          <button
            onClick={() => auth.signOut()}
            style={{
              backgroundColor: '#EF4444',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ padding: '2rem', flex: 1 }}>
        {/* KPI Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Total Capital */}
          <div style={{
            backgroundColor: '#1A1A1A',
            borderRadius: '12px',
            padding: '1.5rem',
            borderTop: '4px solid #8B5CF6'
          }}>
            <div style={{ color: '#999', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Total Capital</div>
            <div style={{ color: 'white', fontSize: '2rem', fontWeight: 'bold' }}>
              {formatCurrency(data?.overview?.totalCapital || 0)}
            </div>
          </div>

          {/* Total Drawn */}
          <div style={{
            backgroundColor: '#1A1A1A',
            borderRadius: '12px',
            padding: '1.5rem',
            borderTop: '4px solid #10B981'
          }}>
            <div style={{ color: '#999', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Total Drawn</div>
            <div style={{ color: 'white', fontSize: '2rem', fontWeight: 'bold' }}>
              {formatCurrency(data?.overview?.totalDrawn || 0)}
            </div>
          </div>

          {/* Available Capital */}
          <div style={{
            backgroundColor: '#1A1A1A',
            borderRadius: '12px',
            padding: '1.5rem',
            borderTop: '4px solid #3B82F6'
          }}>
            <div style={{ color: '#999', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Available Capital</div>
            <div style={{ color: 'white', fontSize: '2rem', fontWeight: 'bold' }}>
              {formatCurrency(data?.overview?.availableCapital || 0)}
            </div>
          </div>

          {/* Utilization Rate */}
          <div style={{
            backgroundColor: '#1A1A1A',
            borderRadius: '12px',
            padding: '1.5rem',
            borderTop: '4px solid #F59E0B'
          }}>
            <div style={{ color: '#999', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Utilization Rate</div>
            <div style={{ color: 'white', fontSize: '2rem', fontWeight: 'bold' }}>
              {data?.overview?.utilizationRate || 0}%
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* AI Insights */}
          <div style={{
            backgroundColor: '#1A1A1A',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              AI Insights
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {data?.insights?.map((insight, index) => (
                <div key={index} style={{
                  backgroundColor: '#2D2D2D',
                  padding: '1rem',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${getInsightColor(insight.type)}`
                }}>
                  <div style={{ color: 'white', fontWeight: '600', marginBottom: '0.25rem' }}>
                    {insight.title}
                  </div>
                  <div style={{ color: '#999', fontSize: '0.875rem' }}>
                    {insight.message}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div style={{
            backgroundColor: '#1A1A1A',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Recommendations
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {data?.recommendations?.map((rec, index) => (
                <div key={index} style={{
                  backgroundColor: '#2D2D2D',
                  padding: '1rem',
                  borderRadius: '8px'
                }}>
                  <div style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>
                    {rec.title}
                  </div>
                  <div style={{ color: '#999', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                    {rec.description}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#10B981', fontSize: '0.875rem' }}>
                      💡 {rec.potentialSavings}
                    </span>
                    <button style={{
                      backgroundColor: '#8B5CF6',
                      color: 'white',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '0.875rem',
                      cursor: 'pointer'
                    }}>
                      {rec.action}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Partner Performance Analysis */}
        <div style={{
          backgroundColor: '#1A1A1A',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            Partner Performance Analysis
          </h2>
          <p style={{ color: '#999', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            Real-time insights from BuildingConnected & TradeTapp integration
          </p>
          
          {/* Partner Cards */}
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {/* KMF Concrete */}
            <div style={{
              backgroundColor: '#2D2D2D',
              borderRadius: '8px',
              padding: '1.5rem',
              borderLeft: '4px solid #10B981'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <h3 style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>KMF Concrete</h3>
                <span style={{ backgroundColor: '#10B981', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.75rem' }}>Active</span>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Projects Won</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>2 locations</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Wichita KS, Omaha NE</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Pipeline</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>{formatCurrency(42100000)}</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>3 bids in progress</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Liquidity Ratio</div>
                  <div style={{ color: '#F59E0B', fontSize: '1rem', fontWeight: '600' }}>0.36</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Needs attention</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Working Cap</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>{formatCurrency(9300000)}</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Utilization: 4.52x</div>
                </div>
              </div>
              
              <div style={{ 
                backgroundColor: '#1A1A1A', 
                padding: '1rem', 
                borderRadius: '6px',
                borderLeft: '3px solid #8B5CF6'
              }}>
                <div style={{ color: 'white', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  💡 Recommended Actions
                </div>
                <div style={{ color: '#999', fontSize: '0.875rem', lineHeight: '1.6' }}>
                  Add project-specific loan; virtual cards for materials; review bonding cost
                </div>
              </div>
            </div>

            {/* Apex Construction */}
            <div style={{
              backgroundColor: '#2D2D2D',
              borderRadius: '8px',
              padding: '1.5rem',
              borderLeft: '4px solid #3B82F6'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <h3 style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>Apex Construction</h3>
                <span style={{ backgroundColor: '#3B82F6', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.75rem' }}>Active</span>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Projects Won</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>1 location</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Omaha NE</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Pipeline</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>{formatCurrency(14500000)}</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>2 bids in progress</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Liquidity Ratio</div>
                  <div style={{ color: '#10B981', fontSize: '1rem', fontWeight: '600' }}>0.70</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Healthy</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Working Cap</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>{formatCurrency(2000000)}</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Utilization: 7.25x</div>
                </div>
              </div>
              
              <div style={{ 
                backgroundColor: '#1A1A1A', 
                padding: '1rem', 
                borderRadius: '6px',
                borderLeft: '3px solid #8B5CF6'
              }}>
                <div style={{ color: 'white', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  💡 Recommended Actions
                </div>
                <div style={{ color: '#999', fontSize: '0.875rem', lineHeight: '1.6' }}>
                  Refinancing LOC; project-level cashflow optimization; optimize bid pipeline
                </div>
              </div>
            </div>

            {/* Crestcon Construction */}
            <div style={{
              backgroundColor: '#2D2D2D',
              borderRadius: '8px',
              padding: '1.5rem',
              borderLeft: '4px solid #EF4444'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <h3 style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>Crestcon Construction</h3>
                <span style={{ backgroundColor: '#EF4444', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.75rem' }}>High Risk</span>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Projects Won</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>0</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>No active projects</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Pipeline</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>{formatCurrency(8300000)}</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>4 bids in progress</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Liquidity Ratio</div>
                  <div style={{ color: '#EF4444', fontSize: '1rem', fontWeight: '600' }}>0.10</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Critical</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Working Cap</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>{formatCurrency(80000)}</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Utilization: 103.75x</div>
                </div>
              </div>
              
              <div style={{ 
                backgroundColor: '#1A1A1A', 
                padding: '1rem', 
                borderRadius: '6px',
                borderLeft: '3px solid #8B5CF6'
              }}>
                <div style={{ color: 'white', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  ⚠️ Urgent Actions Required
                </div>
                <div style={{ color: '#999', fontSize: '0.875rem', lineHeight: '1.6' }}>
                  Recommend short-term bridge loans; increase equity buffer; CFO advisory
                </div>
              </div>
            </div>

            {/* Young, Hall & Tapia */}
            <div style={{
              backgroundColor: '#2D2D2D',
              borderRadius: '8px',
              padding: '1.5rem',
              borderLeft: '4px solid #10B981'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <h3 style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>Young, Hall & Tapia</h3>
                <span style={{ backgroundColor: '#10B981', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.75rem' }}>Stable</span>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Projects Won</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>1 location</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Cleveland OH</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Pipeline</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>{formatCurrency(30600000)}</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>2 bids in progress</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Liquidity Ratio</div>
                  <div style={{ color: '#F59E0B', fontSize: '1rem', fontWeight: '600' }}>0.18</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Below ideal</div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Working Cap</div>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>{formatCurrency(3900000)}</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>Utilization: 7.85x</div>
                </div>
              </div>
              
              <div style={{ 
                backgroundColor: '#1A1A1A', 
                padding: '1rem', 
                borderRadius: '6px',
                borderLeft: '3px solid #8B5CF6'
              }}>
                <div style={{ color: 'white', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  💡 Recommended Actions
                </div>
                <div style={{ color: '#999', fontSize: '0.875rem', lineHeight: '1.6' }}>
                  Solid base, consider bonding re-evaluation; review multi-region credit line
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Project Breakdown */}
        <div style={{
          backgroundColor: '#1A1A1A',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            Project Portfolio
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #333' }}>
                  <th style={{ color: '#999', fontSize: '0.875rem', fontWeight: '500', padding: '0.75rem', textAlign: 'left' }}>Project Name</th>
                  <th style={{ color: '#999', fontSize: '0.875rem', fontWeight: '500', padding: '0.75rem', textAlign: 'right' }}>Loan Amount</th>
                  <th style={{ color: '#999', fontSize: '0.875rem', fontWeight: '500', padding: '0.75rem', textAlign: 'right' }}>Drawn</th>
                  <th style={{ color: '#999', fontSize: '0.875rem', fontWeight: '500', padding: '0.75rem', textAlign: 'right' }}>Remaining</th>
                  <th style={{ color: '#999', fontSize: '0.875rem', fontWeight: '500', padding: '0.75rem', textAlign: 'right' }}>Utilization</th>
                  <th style={{ color: '#999', fontSize: '0.875rem', fontWeight: '500', padding: '0.75rem', textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.projects?.map((project) => (
                  <tr key={project.id} style={{ borderBottom: '1px solid #2D2D2D' }}>
                    <td style={{ color: 'white', padding: '0.75rem' }}>{project.name}</td>
                    <td style={{ color: 'white', padding: '0.75rem', textAlign: 'right' }}>
                      {formatCurrency(project.loanAmount)}
                    </td>
                    <td style={{ color: 'white', padding: '0.75rem', textAlign: 'right' }}>
                      {formatCurrency(project.totalDrawn)}
                    </td>
                    <td style={{ color: 'white', padding: '0.75rem', textAlign: 'right' }}>
                      {formatCurrency(project.remainingBalance)}
                    </td>
                    <td style={{ color: 'white', padding: '0.75rem', textAlign: 'right' }}>
                      {project.utilization}%
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <span style={{
                        backgroundColor: project.status === 'active' ? '#10B981' : '#6B7280',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        textTransform: 'capitalize'
                      }}>
                        {project.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{
        padding: '2rem',
        borderTop: '1px solid #333',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#999',
        fontSize: '0.875rem'
      }}>
        <div>© 2024 ConstructPay. All rights reserved.</div>
        <div>Last updated: {new Date().toLocaleDateString()}</div>
      </footer>
    </div>
  );
}